class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('change', (event) => {
      if (event.target.matches('.anviksha-cart__variant')) this.updateRecommendationVariant(event.target);
    });

    // Reparent the app's original button, retaining its own click listener.
    // Keep its node across section replacements caused by cart updates.
    this.whatsappObserver = new MutationObserver(() => {
      this.syncWhatsAppPosition();
      this.prepareCartRecommendations();
      this.scheduleTierCodeSync();
    });
    this.whatsappObserver.observe(document.body, { childList: true });
    this.whatsappObserver.observe(this, { childList: true, subtree: true });
    this.syncWhatsAppPosition();
    this.prepareCartRecommendations();
    this.scheduleTierCodeSync();
  }

  scheduleTierCodeSync() {
    clearTimeout(this.tierCodeTimer);
    this.tierCodeTimer = setTimeout(() => this.syncTierCode(), 250);
  }

  // Keep exactly one BUYMORE tier code on the Shopify cart, as chosen by snippets/anviksha-cart-offers.liquid.
  // Shopify applies (or rejects) the code itself, so totals and GoKwik always follow the real cart.
  // - The tier code replaces only the public promotional codes listed in data-public-promo-codes.
  // - Other (private / customer-specific) codes are never removed; while one applies, no tier code is added.
  // - If Shopify does not apply the tier code (e.g. it doesn't exist yet), the previous codes are restored,
  //   so a valid discount is never swapped for one that gives nothing.
  async syncTierCode() {
    const sync = this.querySelector('[data-tier-code-sync]');
    if (!sync || this.tierCodeSyncing) return;
    this.tierCodeSyncing = true;
    let pending = false;
    try {
      const upper = (code) => code.toUpperCase();
      const prefix = upper(sync.dataset.tierCodePrefix);
      const publicCodes = (sync.dataset.publicPromoCodes || '').split(',').map((code) => upper(code.trim()));
      const isTierCode = (discount) => upper(discount.code).startsWith(prefix);
      const isPublicCode = (discount) => publicCodes.includes(upper(discount.code));
      const cart = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((response) =>
        response.json()
      );
      const current = cart.discount_codes || [];
      const privateCodes = current.filter((discount) => !isTierCode(discount) && !isPublicCode(discount));
      const targetCode = upper(sync.dataset.tierCode || '');
      const useTier = targetCode && !privateCodes.some((discount) => discount.applicable);
      const withoutTier = current.filter((discount) => !isTierCode(discount)).map((discount) => discount.code);
      const desired = useTier ? privateCodes.map((discount) => discount.code).concat(targetCode) : withoutTier;
      const currentCodes = current.map((discount) => discount.code);
      if (currentCodes.map(upper).join(',') === desired.map(upper).join(',')) return;

      // Never retry the same change in a loop if Shopify keeps the cart as it is.
      const attempt = `${currentCodes.join(',')}>${desired.join(',')}`;
      if (this.tierCodeLastAttempt === attempt) return;
      this.tierCodeLastAttempt = attempt;

      pending = true;
      this.setCartPending(true);
      const update = (codes) =>
        fetch(`${routes.cart_update_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            discount: codes.join(','),
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        }).then((response) => response.json());
      let state = await update(desired);
      const tierApplied =
        !useTier ||
        (state.discount_codes || []).some((discount) => upper(discount.code) === targetCode && discount.applicable);
      if (!tierApplied) state = await update(withoutTier);
      // A change Shopify accepted may be needed again later (e.g. the code was cleared elsewhere);
      // only a change Shopify refused stays blocked.
      const resultCodes = (state.discount_codes || []).map((discount) => upper(discount.code)).join(',');
      if (tierApplied && resultCodes === desired.map(upper).join(',')) this.tierCodeLastAttempt = null;
      if (state.sections) this.renderContents(state, false);
    } catch (error) {
      // Leave the cart as Shopify has it; the drawer only ever shows Shopify's real totals.
    } finally {
      this.tierCodeSyncing = false;
      if (pending) this.setCartPending(false);
    }
  }

  prepareCartRecommendations() {
    if (this.recommendationList && !this.contains(this.recommendationList)) {
      this.recommendationResizeObserver?.disconnect();
      this.recommendationList = null;
    }
    this.querySelectorAll('.anviksha-cart__recommendations-list:not([data-aov-ready])').forEach((list) => {
      list.dataset.aovReady = 'true';
      const recommendations = list.closest('product-recommendations');
      const remaining = Number(recommendations.dataset.offerRemaining);
      let cards = Array.from(list.children);
      const rank = (card) => Number(card.dataset.sourceRank);
      // Complete the look: favour piece types the cart doesn't already have (earrings with a necklace, etc.).
      const cartTypes = (recommendations.dataset.cartTypes || '').toLowerCase().split('|').filter(Boolean);
      const sameType = (card) => (cartTypes.includes((card.dataset.productType || '').toLowerCase()) ? 1 : 0);
      if (recommendations.dataset.offerActive === 'true' && remaining > 0 && recommendations.dataset.tiers) {
        // Achievable first: pieces that just reach the next reward (up to ~45% / ₹300 above it, never
        // capped below ₹999), then pieces just short of it, then pricier ones, then much cheaper ones.
        // Keep the window in sync with snippets/anviksha-cart-recommendations.liquid.
        const price = (card) => Number(card.dataset.recommendationPrice);
        const distance = (card) => Math.abs(price(card) - remaining);
        const cap = Math.max(remaining * 1.45, remaining + 30000, 99900);
        const band = (card) => {
          const value = price(card);
          if (value >= remaining && value <= cap) return 0;
          if (value >= remaining * 0.8 && value < remaining) return 1;
          if (value > cap) return 2;
          return 3;
        };
        cards.sort(
          (a, b) => band(a) - band(b) || sameType(a) - sameType(b) || distance(a) - distance(b) || rank(a) - rank(b)
        );
      } else if (recommendations.dataset.offerActive === 'true' && remaining > 0) {
        const price = (card) => Number(card.dataset.recommendationPrice);
        const distance = (card) => Math.abs(price(card) - remaining);
        const lastRank = Math.max(1, ...cards.map(rank));
        const score = (card) =>
          0.35 * rank(card) / lastRank + 0.2 * Math.min(1, distance(card) / remaining) + 0.45 * sameType(card);
        cards.sort((a, b) => score(a) - score(b) || rank(a) - rank(b));
        const selected = cards.slice(0, 4);
        const include = (card) => {
          if (card && !selected.includes(card)) selected.push(card);
        };
        const affordable = cards.filter((card) => price(card) <= remaining);
        include(affordable.slice().sort((a, b) => distance(a) - distance(b) || rank(a) - rank(b))[0]);
        include(cards.slice().sort((a, b) => price(a) - price(b) || rank(a) - rank(b))[0]);
        cards.forEach((card) => {
          if (selected.length < 6) include(card);
        });
        cards = selected.concat(cards.filter((card) => !selected.includes(card)));
      } else {
        cards.sort((a, b) => sameType(a) - sameType(b) || rank(a) - rank(b));
      }
      cards.slice(6).forEach((card) => card.remove());
      // Move the original cards into pairs; their product forms and listeners stay intact.
      const selectedCards = cards.slice(0, 6);
      const pages = [];
      for (let index = 0; index < selectedCards.length; index += 2) {
        const page = document.createElement('li');
        page.className = 'anviksha-cart__recommendation-page';
        page.setAttribute('role', 'presentation');
        const pair = document.createElement('ul');
        pair.className = 'anviksha-cart__recommendation-pair list-unstyled';
        selectedCards.slice(index, index + 2).forEach((card) => pair.appendChild(card));
        page.appendChild(pair);
        list.appendChild(page);
        pages.push(page);
      }

      // Fit the visible pair instead of reserving height for the tallest off-screen card.
      // During a swipe, allow both intersecting pairs to fit without clipping their buttons.
      const sizeRail = () => {
        const rail = list.getBoundingClientRect();
        const visible = pages.map((page) => page.firstElementChild.getBoundingClientRect())
          .filter((page) => Math.min(page.right, rail.right) - Math.max(page.left, rail.left) > 1);
        if (!visible.length) return;
        const style = getComputedStyle(list);
        const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
        const height = `${Math.ceil(Math.max(...visible.map((page) => page.height)) + padding)}px`;
        if (list.style.height !== height) list.style.height = height;
      };
      list.addEventListener('scroll', sizeRail, { passive: true });
      this.recommendationResizeObserver?.disconnect();
      this.recommendationResizeObserver = new ResizeObserver(sizeRail);
      this.recommendationResizeObserver.observe(list);
      pages.forEach((page) => this.recommendationResizeObserver.observe(page.firstElementChild));
      this.recommendationList = list;
      sizeRail();
    });
  }

  updateRecommendationVariant(select) {
    const option = select.selectedOptions[0];
    const card = select.closest('.anviksha-cart__recommendation');
    if (!option || !card) return;
    const price = Number(option.dataset.price);
    const compare = Number(option.dataset.compare);
    const saving = Number(option.dataset.saving);
    card.dataset.recommendationPrice = price;
    card.querySelector('[data-recommendation-price-label]').textContent = option.dataset.priceMoney;
    const compareLabel = card.querySelector('[data-recommendation-compare]');
    compareLabel.textContent = option.dataset.compareMoney;
    compareLabel.hidden = compare <= price;
    const savingLabel = card.querySelector('[data-recommendation-saving]');
    savingLabel.textContent = `${saving}% off`;
    savingLabel.hidden = saving <= 0;

    const recommendations = card.closest('product-recommendations');
    const remaining = Number(recommendations.dataset.offerRemaining);
    const active = recommendations.dataset.offerActive === 'true';
    const reachesGoal = active && remaining > 0 && price >= remaining;
    const addButton = card.querySelector('.anviksha-cart__recommendation-add');
    addButton?.classList.toggle('anviksha-cart__recommendation-add--unlock', reachesGoal);
    let reachLabel = recommendations.dataset.reachLabel || 'Add to Reach';
    let reachOfferLabel = recommendations.dataset.reachOfferLabel || 'Reaches the spending goal';
    if (reachesGoal && recommendations.dataset.tiers) {
      // Name the best reward this variant actually unlocks.
      const after = Number(recommendations.dataset.eligibleSubtotal) + price;
      const unlocked = recommendations.dataset.tiers
        .split(',')
        .map((tier) => tier.split(':').map(Number))
        .filter(([threshold]) => after >= threshold)
        .pop();
      if (unlocked) {
        reachLabel = `Add & Unlock ${unlocked[1]}%`;
        reachOfferLabel = `Unlocks ${unlocked[1]}% off`;
      }
    }
    card.querySelector('[data-recommendation-add-label]').textContent = reachesGoal ? reachLabel : '+ Add';
    const offerLabel = card.querySelector('[data-recommendation-offer]');
    if (offerLabel) {
      offerLabel.textContent = reachesGoal
        ? reachOfferLabel
        : recommendations.dataset.eligibleLabel || 'Eligible for SHOP10';
    }
  }

  syncWhatsAppPosition() {
    if (!this.whatsappNode) {
      this.whatsappNode = document.getElementById('carthike-chat-wrapper');
      if (!this.whatsappNode) return;
      this.whatsappHome = document.createComment('Original WhatsApp app position');
      this.whatsappNode.before(this.whatsappHome);
    }

    const slot = this.querySelector('.anviksha-cart__whatsapp');
    if (this.classList.contains('active') && !this.classList.contains('is-empty') && slot) {
      if (this.whatsappNode.parentNode !== slot) slot.appendChild(this.whatsappNode);
    } else if (this.whatsappHome.parentNode && this.whatsappNode.previousSibling !== this.whatsappHome) {
      this.whatsappHome.after(this.whatsappNode);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.ensureProductForms();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.syncWhatsAppPosition();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.syncWhatsAppPosition();
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  setCartPending(pending) {
    this.pendingRequests = Math.max(0, (this.pendingRequests || 0) + (pending ? 1 : -1));
    this.applyCartPending();
  }

  applyCartPending() {
    const pending = (this.pendingRequests || 0) > 0;
    this.toggleAttribute('data-cart-pending', pending);
    this.querySelector('.cart__ctas')?.toggleAttribute('inert', pending);
  }

  ensureProductForms() {
    if (customElements.get('product-form')) return;
    const source = this.dataset.productFormUrl && new URL(this.dataset.productFormUrl, window.location.href).href;
    if (!source || Array.from(document.scripts).some((script) => script.src === source)) return;
    const script = document.createElement('script');
    script.src = source;
    script.defer = true;
    document.head.appendChild(script);
  }

  renderContents(parsedState, shouldOpen = true) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.classList.toggle('is-empty', Boolean(this.querySelector('.drawer__inner-empty')));
    this.applyCartPending();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (shouldOpen) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    this.closest('cart-drawer')?.applyCartPending();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Both existing paths already render the server-returned sections.
      if (event.source === 'cart-items' || event.source === 'product-form') return;
      return this.onCartUpdate(event);
    });
  }

  onCartUpdate(event) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return Promise.resolve();
    if (event?.cartData?.sections?.['cart-drawer'] && event.cartData.sections['cart-icon-bubble']) {
      drawer.renderContents(event.cartData, false);
      return Promise.resolve();
    }
    return fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
      .then((response) => {
        if (!response.ok) throw new Error('Unable to refresh the cart drawer');
        return response.json();
      })
      .then((sections) => drawer.renderContents({ sections }, false))
      .catch(() => {
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      });
  }

  enableLoading(line) {
    document.querySelector('cart-drawer')?.setCartPending(true);
    super.enableLoading(line);
  }

  disableLoading(line) {
    super.disableLoading(line);
    document.querySelector('cart-drawer')?.setCartPending(false);
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
